import { useState, useEffect, useCallback, useRef } from 'react';
import { getScanIntentToken, getScanIntentId, clearScanIntentToken, clearScanIntentId, clearUnlockIntent, readScanStatus, watchScan } from '@privisy/ui-bridge';
import { API_BASE, claimScan, ClaimError } from '../utils/api';
import { parseAudience, rememberAudience, type Audience } from '../utils/audience';

export interface ClaimParams {
  scanId: string | null;
  intentToken: string | null;
  domain: string | null;
  score: number | null;
  grade: string | null;
  /** #2046: `audience=consumer` from /check, allowlisted. */
  audience: Audience | null;
}

const EMPTY_CLAIM_PARAMS: ClaimParams = { scanId: null, intentToken: null, domain: null, score: null, grade: null, audience: null };

export interface ClaimResult {
  type: 'success' | 'error';
  message: string;
  claimedScanId?: string;
}

function sanitizeClaimParams(params: URLSearchParams): ClaimParams {
  const scanId = params.get('claimScanId');
  const intentToken = params.get('intentToken');

  // Sanitize domain: strip angle brackets, truncate to 253 chars
  const rawDomain = params.get('domain') ?? null;
  const domain = rawDomain
    ? rawDomain.replace(/[<>]/g, '').substring(0, 253) || null
    : null;

  // Sanitize score: clamp 0-100
  const rawScore = params.get('score');
  let score: number | null = null;
  if (rawScore !== null) {
    const parsed = parseInt(rawScore, 10);
    if (!isNaN(parsed)) score = Math.max(0, Math.min(100, parsed));
  }

  // Sanitize grade: allowlist A/B/C/D/F only
  const rawGrade = params.get('grade');
  const grade = rawGrade && /^[ABCDF]$/.test(rawGrade) ? rawGrade : null;

  const audience = parseAudience(params.get('audience'));

  return { scanId, intentToken, domain, score, grade, audience };
}

/**
 * Resolve the scanId + intent token to claim with.
 *
 * URL params take precedence; the cross-subdomain cookie values are the durable
 * fallback used when the sign-up email-verification redirect drops the URL
 * params (it returns to a clean URL with no ?claimScanId=/intentToken=). Keeping
 * this pure makes the fallback contract directly testable.
 */
export function resolveClaimIdentity(args: {
  urlScanId: string | null;
  urlToken: string | null;
  persistedScanId: string | null;
  persistedToken: string | null;
}): { scanId: string | null; token: string | null } {
  return {
    scanId: args.urlScanId || args.persistedScanId || null,
    token: args.urlToken || args.persistedToken || null,
  };
}

function mapClaimError(err: unknown): string {
  if (err instanceof ClaimError) {
    if (err.status === 404) {
      return 'Scan not found. It may have expired. Start a new scan.';
    }
    // Switch on the typed code (#1695); the status is only the fallback.
    switch (err.apiCode) {
      case 'SCAN_ALREADY_CLAIMED':
        return 'This scan is already in your account.';
      case 'INVALID_INTENT_TOKEN':
        return 'This scan link has expired. Start a new scan.';
      case 'SCAN_NOT_FOUND':
        return 'Scan not found. It may have expired. Start a new scan.';
    }
    if (err.status === 403) {
      return 'Access denied. Start a new scan.';
    }
    if (err.status >= 500) {
      return 'Connection failed. Check your network and try again.';
    }
  }
  return 'Unable to load your scan results. The scan may have expired or been claimed already. Please scan your site again.';
}

// One claim request per scanId, shared across hook instances, and its outcome
// acted on once. handleClaim can run twice for one claim: dev StrictMode
// re-runs the claim effect before `isClaiming` re-renders, and index.tsx
// unmounts <App> while Clerk's isLoaded is false (around sign-up), so a claim
// can be in flight in an instance that is gone by the time it resolves. Every
// run awaits the same request; the first mounted one to resume handles the
// outcome, so onClaimSuccess (the free-audit auto-start) fires once.
const inFlightClaims = new Map<string, { request: Promise<unknown>; handled: boolean }>();

/**
 * #1814: a claim answers SCAN_NOT_FOUND until the scan settles, because the
 * claim needs the `reports` row and settlement writes it. True only when the
 * public status lane says the scan is still queued or running. A genuine 404,
 * or a probe that fails, keeps the old terminal handling.
 */
async function isStillRunning(err: unknown, scanId: string): Promise<boolean> {
  if (!(err instanceof ClaimError)) return false;
  if (err.apiCode !== 'SCAN_NOT_FOUND' && err.status !== 404) return false;
  try {
    return (await readScanStatus({ apiBase: API_BASE, scanId })) === 'pending';
  } catch {
    return false;
  }
}

/** The scan id in a `#/scan/:id` hash, if that's the route. */
function scanIdFromHash(hash: string): string | null {
  const match = /^#\/scan\/([^/?]+)/.exec(hash);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}

export function useClaimScan(
  isSignedIn: boolean | undefined,
  authEnabled: boolean,
  setHashRoute: (route: string) => void,
  /**
   * Fired exactly once, right where the claim-success transition happens
   * (issue #877), with the claimed scanId. `handleClaim` only ever reaches
   * this line once per successful claim — the effect below re-invokes it
   * only while `claimParams.scanId` is still set, and `clearClaimParams()`
   * nulls that out earlier in this same success branch — and a second run
   * for the same claim never reaches it (see inFlightClaims), so callers
   * don't need a guard of their own against a double auto-start.
   */
  onClaimSuccess?: (scanId: string) => void,
  /**
   * #1779: the claim answered SCAN_ALREADY_CLAIMED. A second tab, or a
   * reload after the claim landed, can be the same user, so the unlock
   * intent survives this one terminal error and the caller decides with an
   * authenticated read: owning the scan proves it, anything else clears it.
   */
  onAlreadyClaimed?: (scanId: string) => void,
) {
  const [claimParams, setClaimParams] = useState<ClaimParams>(() => {
    if (typeof window === 'undefined') return EMPTY_CLAIM_PARAMS;
    const params = new URLSearchParams(window.location.search);
    const sanitized = sanitizeClaimParams(params);
    // #2046: stored now, before the claim strips the URL params, so the
    // report keeps its framing through sign-up and the unlock round trip.
    // Idempotent, so a StrictMode double run is harmless.
    if (sanitized.scanId && sanitized.audience) rememberAudience(sanitized.scanId, sanitized.audience);
    return sanitized;
  });

  const [isClaiming, setIsClaiming] = useState(false);
  const [claimResult, setClaimResult] = useState<ClaimResult | null>(null);

  const clearClaimParams = useCallback(() => {
    setClaimParams(EMPTY_CLAIM_PARAMS);
  }, []);

  const clearUrlParams = useCallback(() => {
    window.history.replaceState({}, '', window.location.pathname + window.location.hash);
  }, []);

  const mountedRef = useRef(false);
  // #1814: the scan a SCAN_NOT_FOUND claim is waiting on, with the token it
  // used and the watch to dispose. One at a time.
  const waitingRef = useRef<{ scanId: string; dispose: () => void } | null>(null);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      waitingRef.current?.dispose();
      waitingRef.current = null;
    };
  }, []);

  // Claim again once the scan settles. The cookies were kept, so the retry
  // resolves the same identity; a URL token is carried over explicitly.
  const retryAfterSettlement = useCallback((scanId: string, intentToken: string) => {
    waitingRef.current?.dispose();
    const retry = () => {
      if (waitingRef.current?.scanId !== scanId) return;
      waitingRef.current.dispose();
      waitingRef.current = null;
      if (!mountedRef.current) return;
      setClaimParams((prev) => ({ ...prev, scanId, intentToken }));
    };
    // Either end retries: a failed scan settles too, and the claim's own
    // answer then decides what the visitor sees.
    const dispose = watchScan({
      apiBase: API_BASE,
      scanId,
      transport: 'poll',
      onComplete: retry,
      onFailure: retry,
    });
    waitingRef.current = { scanId, dispose };
  }, []);

  const handleClaim = useCallback(async () => {
    if (!isSignedIn || !authEnabled || isClaiming || !claimParams.scanId) return;

    const { scanId: scanIdToUse, token } = resolveClaimIdentity({
      urlScanId: claimParams.scanId,
      urlToken: claimParams.intentToken,
      persistedScanId: getScanIntentId(),
      persistedToken: getScanIntentToken(),
    });

    if (!scanIdToUse || !token) {
      setClaimResult({
        type: 'error',
        message: "We couldn't find your scan session. This can happen if you open the link on a different device or browser without the direct link. Please scan your site again to get the full report."
      });
      // No usable identity — clear any orphaned persisted values so the claim
      // card doesn't keep re-appearing on subsequent loads.
      clearScanIntentToken();
      clearScanIntentId();
      clearUnlockIntent();
      clearClaimParams();
      // Remove query params from URL
      clearUrlParams();
      return;
    }

    let claim = inFlightClaims.get(scanIdToUse);
    if (!claim) {
      claim = {
        request: claimScan(scanIdToUse, token).finally(() => inFlightClaims.delete(scanIdToUse)),
        handled: false,
      };
      inFlightClaims.set(scanIdToUse, claim);
    }
    // True for exactly one mounted run per claim; every other run stands down.
    const takeOutcome = (c: { handled: boolean }) => {
      if (!mountedRef.current || c.handled) return false;
      c.handled = true;
      return true;
    };

    try {
      setIsClaiming(true);
      await claim.request;
      if (!takeOutcome(claim)) return;

      clearScanIntentToken();
      clearScanIntentId();
      clearClaimParams();
      
      // Remove query params from URL
      clearUrlParams();
      
      setClaimResult({
        type: 'success',
        message: 'Scan successfully associated with your account.',
        claimedScanId: scanIdToUse,
      });

      setHashRoute(`#/scan/${encodeURIComponent(scanIdToUse)}`);
      onClaimSuccess?.(scanIdToUse);
    } catch (err) {
      if (!takeOutcome(claim)) return;
      if (await isStillRunning(err, scanIdToUse)) {
        // Not terminal (#1814): keep the cookies and the URL, show nothing,
        // and claim again when the scan settles.
        if (!mountedRef.current) return;
        clearClaimParams();
        retryAfterSettlement(scanIdToUse, token);
        return;
      }
      if (!mountedRef.current) return;
      const message = mapClaimError(err);
      // Check if this is a "already claimed" error — extract scanId from error if possible
      const isAlreadyClaimed = err instanceof ClaimError && err.apiCode === 'SCAN_ALREADY_CLAIMED';
      // Clear persisted identity on terminal failures so a reload doesn't
      // re-attempt a claim that can never succeed. Keep cookies for transient
      // failures (5xx AND non-ClaimError like network timeouts) so a reload
      // can still retry.
      const isTransient = !(err instanceof ClaimError) || err.status >= 500;
      if (!isTransient) {
        clearScanIntentToken();
        clearScanIntentId();
        if (!isAlreadyClaimed) clearUnlockIntent();
      }
      setClaimResult({
        type: 'error',
        message,
        claimedScanId: isAlreadyClaimed ? scanIdToUse : undefined,
      });
      clearClaimParams();
      clearUrlParams();
      if (isAlreadyClaimed) onAlreadyClaimed?.(scanIdToUse);
    } finally {
      setIsClaiming(false);
    }
  }, [isSignedIn, authEnabled, isClaiming, claimParams, clearClaimParams, clearUrlParams, setHashRoute, onClaimSuccess, onAlreadyClaimed, retryAfterSettlement]);

  // Recover a claim dropped during the sign-up email-verification redirect.
  // That redirect returns to the scanner on a clean URL (no ?claimScanId=), so
  // we re-hydrate the scanId from the cross-subdomain cookie the marketing site
  // persisted. Runs at most once per mount so a failed claim — which nulls
  // claimParams.scanId via clearClaimParams — can't spin into a retry loop.
  const recoveredRef = useRef(false);
  useEffect(() => {
    if (!isSignedIn || !authEnabled) return;
    if (claimParams.scanId || recoveredRef.current) return;
    // A claim already waiting on settlement (#1814) retries on its own.
    if (waitingRef.current) return;
    const persistedScanId = getScanIntentId();
    if (persistedScanId) {
      recoveredRef.current = true;
      setClaimParams((prev) => ({ ...prev, scanId: persistedScanId }));
    }
  }, [isSignedIn, authEnabled, claimParams.scanId]);

  // #1814: a signed-in visitor who starts the anonymous audit in this session
  // (useStartLiteScan sets the intent cookies, then routes to #/scan/:id) never
  // remounts this hook, so the recovery above never sees those cookies. Claim
  // when the route lands on the scan the cookie names; the claim then waits
  // for settlement (see isStillRunning). After a claim's terminal answer the
  // cookies are gone, so this can't loop.
  useEffect(() => {
    if (!isSignedIn || !authEnabled) return;
    const onHashChange = () => {
      const routed = scanIdFromHash(window.location.hash);
      if (!routed || waitingRef.current?.scanId === routed) return;
      if (routed !== getScanIntentId()) return;
      setClaimParams((prev) => (prev.scanId ? prev : { ...prev, scanId: routed }));
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [isSignedIn, authEnabled]);

  useEffect(() => {
    if (isSignedIn && claimParams.scanId) {
      handleClaim();
    }
  }, [isSignedIn, claimParams.scanId, handleClaim]);

  return {
    claimParams,
    isClaiming,
    claimResult,
    setClaimResult,
    handleClaim,
    clearClaimParams
  };
}
